import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { ResponsiveImage, Modal } from '../../../components';
import ImageCarousel from '../ImageCarousel/ImageCarousel';
import { IconPhotos } from '../ListingIcons';

import css from './ListingImageMosaic.module.css';

const IMAGE_VARIANTS = ['scaled-small', 'scaled-medium', 'scaled-large', 'scaled-xlarge'];

/**
 * Figma-style mosaic: one large image + up to two stacked thumbnails + "Show all Photos".
 * Only renders side slots when real images exist (no duplicated placeholders).
 *
 * @component
 */
const ListingImageMosaic = props => {
  const intl = useIntl();
  const { images = [], onManageDisableScrolling } = props;
  const [carouselOpen, setCarouselOpen] = useState(false);

  if (!images.length) {
    return <ResponsiveImage className={css.noImage} image={null} variants={[]} alt="" />;
  }

  const main = images[0];
  const sideTop = images[1] || null;
  const sideBottom = images[2] || null;
  const hasSideColumn = Boolean(sideTop);
  const hasMultiple = images.length > 1;

  const altFor = index =>
    intl.formatMessage(
      { id: 'ListingImageGallery.imageAltText' },
      { index: index + 1, count: images.length }
    );

  const openCarousel = () => setCarouselOpen(true);

  return (
    <>
      <div
        className={classNames(css.root, {
          [css.singleImage]: !hasSideColumn,
          [css.twoImages]: images.length === 2,
          [css.threeImages]: images.length > 2,
        })}
      >
        <button type="button" className={css.mainImage} onClick={openCarousel}>
          <ResponsiveImage
            rootClassName={css.image}
            image={main}
            alt={altFor(0)}
            variants={IMAGE_VARIANTS}
            sizes={hasSideColumn ? '(max-width: 768px) 100vw, 1240px' : '100vw'}
          />
        </button>

        {hasSideColumn ? (
          <div className={css.sideColumn}>
            <button type="button" className={css.sideImage} onClick={openCarousel}>
              <ResponsiveImage
                rootClassName={css.image}
                image={sideTop}
                alt={altFor(1)}
                variants={IMAGE_VARIANTS}
                sizes="400px"
              />
            </button>
            {sideBottom ? (
              <div className={css.sideImageBottom}>
                <button type="button" className={css.sideImage} onClick={openCarousel}>
                  <ResponsiveImage
                    rootClassName={css.image}
                    image={sideBottom}
                    alt={altFor(2)}
                    variants={IMAGE_VARIANTS}
                    sizes="400px"
                  />
                </button>
                {hasMultiple ? (
                  <button type="button" className={css.showAllButton} onClick={openCarousel}>
                    <span className={css.showAllIcon}>
                      <IconPhotos />
                    </span>
                    <FormattedMessage id="ListingPage.showAllPhotos" />
                  </button>
                ) : null}
              </div>
            ) : hasMultiple ? (
              <button type="button" className={css.showAllButtonStandalone} onClick={openCarousel}>
                <span className={css.showAllIcon}>
                  <IconPhotos />
                </span>
                <FormattedMessage id="ListingPage.showAllPhotos" />
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <Modal
        id="ListingImageMosaic.carousel"
        scrollLayerClassName={css.carouselModalScrollLayer}
        containerClassName={css.carouselModalContainer}
        lightCloseButton
        isOpen={carouselOpen}
        onClose={() => setCarouselOpen(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <ImageCarousel images={images} imageVariants={IMAGE_VARIANTS} />
      </Modal>
    </>
  );
};

export default ListingImageMosaic;
